<template>
  <v-sheet color="white" height="100%" class="rounded-lg shadow pa-0 ma-0">
    <div v-show="expanded">
      <v-container
        v-if="
          odds_ratio_request_status == null ||
            odds_ratio_request_status == 'loading' ||
            odds_ratio_error != null
        "
        fluid
        fill-height
      >
        <v-row>
          <v-col cols="12">
            <v-subheader class="subheading primary--text text--lighten-4">
              <div v-if="odds_ratio_request_status == null">
                Add variables and apply filters to them to view regression
                analysis results for all predictor variables.
              </div>
              <div v-else-if="odds_ratio_request_status == 'loading'">
                <v-subheader class="title primary--text text--lighten-4">
                  <loading-spinner />
                </v-subheader>
              </div>
              <div v-else-if="odds_ratio_error != null">
                <v-subheader class="title primary--text">
                  <v-icon color="warning" medium class="pb-1 pr-2"
                    >warning</v-icon
                  >
                  Regression analysis failed: {{ odds_ratio_error }}
                </v-subheader>
              </div>
            </v-subheader>
          </v-col>
        </v-row>
      </v-container>

      <v-container v-else fluid fill-height class="ma-0 pa-0">
        <v-row>
          <v-col cols="12" class="pb-0 ml-2">
            <v-chip
              v-if="showHelpChip"
              label
              close
              color="#4caf50"
              class="font-weight-bold white--text pa-3 mb-2"
              @click:close="showHelpChip = false"
              >Review regression analysis for Selected Study Group vs.
              Remainder.</v-chip
            >

            <v-row class="ma-0 pa-0">
              <div style="display: flex;">
                <v-toolbar-title
                  class="primary--text subtitle-1 font-weight-bold mb-1"
                  >Comparing</v-toolbar-title
                >

                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <img
                      :src="'/images/' + variable.category + '-icon-128.png'"
                      :title="variable.category"
                      style="height:1.25em"
                      class="ma-1 ml-2"
                      @error="missingDomainFallback"
                      v-on="{ ...tooltip }"
                    />
                  </template>
                  <span>{{ variable.category }}</span>
                </v-tooltip>

                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span
                      class="black--text subtitle-1 mb-1"
                      v-on="{ ...tooltip }"
                    >
                      {{
                        variable.abbreviation === variable.label
                          ? useLongScaleNames
                            ? variable.label
                            : variable.abbreviation
                          : useLongScaleNames
                          ? variable.parentLabel
                          : variable.abbreviation
                      }}
                    </span>
                  </template>
                  <span>{{ variable.description }}</span>
                </v-tooltip>

                <v-toolbar-title
                  class="primary--text subtitle-1 font-weight-bold mb-1 ml-1"
                  >likelihood between:</v-toolbar-title
                >
              </div>
            </v-row>

            <v-row class="ma-0 pa-0">
              <span>
                <v-chip
                  label
                  color="primary"
                  class="white--text title mr-2 mb-2"
                  :style="'background: ' + colors['cohort']"
                  >{{ cohortSize }}</v-chip
                >
                <span class="black--text text-body-1"
                  >Selected&nbsp;Study&nbsp;Group | Target</span
                >
              </span>
            </v-row>
            <v-row class="ma-0 pa-0">
              <ul class="ml-4">
                <li
                  v-for="(dname, index) in Object.keys(queries).sort()"
                  :key="'atsq-' + index"
                >
                  <span class="subtitle-1">
                    {{ queryToString(dname, queries[dname]) }}
                  </span>
                </li>
              </ul>
            </v-row>

            <v-row class="ma-0 pa-0 pt-2">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <v-chip
                      label
                      class="black--text title mr-2"
                      :style="'background: ' + colors['population']"
                      >{{ remainderSize }}</v-chip
                    >
                    <span class="black--text text-body-1"
                      >Remainder | Control (all other subjects)</span
                    >
                  </span>
                </template>
                <span class="subtitle-1">
                  {{ remainderSize }} subject{{
                    remainderSize == 1 ? '' : 's'
                  }}
                  in remainder
                </span>
              </v-tooltip>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="ma-0 pa-0 mt-4">
          <div class="ma-0 pa-0" style="display: flex;">
            <span class="pa-0 ma-2">Show:</span>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-checkbox
                    v-model="showTooltips"
                    class="my-checkbox pa-0 ma-0 mt-2"
                    label="tooltips"
                    dense
                    hide-details
                  ></v-checkbox>
                </span>
              </template>
              <span v-if="true">Show tooltips</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-checkbox
                    v-model="showErrorBars"
                    class="my-checkbox pa-0 ma-0 pl-3 mt-2"
                    label="error bars"
                    dense
                    hide-details
                  ></v-checkbox>
                </span>
              </template>
              <span v-if="true">Show error bars</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-checkbox
                    v-model="showOdds"
                    class="my-checkbox pa-0 ma-0 pl-3 mt-2"
                    label="odds ratio"
                    dense
                    hide-details
                  ></v-checkbox>
                </span>
              </template>
              <span v-if="true">Show odds ratio with 95% CI</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-checkbox
                    v-model="showPVals"
                    class="my-checkbox pa-0 ma-0 pl-3 mt-2"
                    label="p-values"
                    dense
                    hide-details
                  ></v-checkbox>
                </span>
              </template>
              <span v-if="true">Show p-values</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-checkbox
                    v-model="showLogScale"
                    class="my-checkbox pa-0 ma-0 pl-3 mt-2"
                    label="log scale"
                    dense
                    hide-details
                  ></v-checkbox>
                </span>
              </template>
              <span v-if="true">Show log scale on x-axis</span>
            </v-tooltip>
          </div>
        </v-row>

        <v-row class="ma-0 pa-0 mb-4 mt-4">
          <v-col cols="12" class="pa-0 ma-0 mt-n4">
            <span v-if="hasCategorical" class="text-subtitle-1 ma-0 ml-2 pa-0"
              >Asterisk (*) indicates reference category for categorical
              variables.</span
            >

            <PlotlyChart
              :data="chartData"
              :layout="chartLayout"
              class="ma-0 pa-0 mt-2"
            />

            <div class="ml-2 mt-2">
              <v-chip
                v-if="showInterpretation"
                label
                close
                color="#4caf50"
                class="font-weight-bold white--text pa-3 mr-2"
                style="height: auto; white-space: normal;"
                @click:close="showInterpretation = false"
                ><span
                  >If the odds ratio is &gt; 1, there is a positive correlation
                  between the predictor and outcome variable, indicating that
                  the predictor is a <u>risk factor</u> for membership in the
                  selected study group (increased likelihood/right side of the
                  plot). If the odds ratio is &lt; 1, there is a negative
                  correlation between the predictor and outcome variable,
                  indicating that the predictor is a
                  <u>protecting factor</u> making it less likely that an
                  individual will be in the selected study group (decreased
                  likelihood/left side of the plot).</span
                ></v-chip
              >
            </div>
          </v-col>
        </v-row>

        <v-divider v-if="hasErrors" class="ml-4 mr-4"></v-divider>

        <v-row class="ma-0 pa-0">
          <v-expansion-panels
            v-if="odds_ratio_request_status == 'loaded' && hasErrors"
            accordion
            flat
          >
            <!-- Show Individual subject -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="text-subtitle-1"
                  >Show regression analysis errors</span
                >
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table
                  ref="table"
                  :headers="headers"
                  :items="odds_ratio"
                  :must-sort="true"
                  dense
                  hide-default-footer
                  disable-pagination
                >
                  <template v-slot:item="props">
                    <tr v-if="props.item.error">
                      <td class="text-subtitle-1 text-xs-left">
                        <v-tooltip top color="primary">
                          <template v-slot:activator="{ on: tooltip }">
                            <span v-on="{ ...tooltip }">
                              {{ props.item.abbreviation }}
                            </span>
                          </template>
                          <span> {{ props.item.label }} </span>
                        </v-tooltip>
                      </td>
                      <td class="text-subtitle-1 text-xs-left">
                        <v-icon color="warning" medium class="pb-1"
                          >warning</v-icon
                        >
                        {{ props.item.error }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-row>
      </v-container>
    </div>
  </v-sheet>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { state, actions } from '@/store/modules/cohortManager/types';
import { format } from 'd3-format';
import { colors } from '@/utils/colors';
import { queryToString, missingDomainFallback } from '@/utils/helpers';
import PlotlyChart from '@/components/common/PlotlyChart.vue';

export default {
  components: {
    PlotlyChart,
  },
  filters: {
    formatPValue(pvalue) {
      if (pvalue == null) {
        return '-';
      }
      if (pvalue < 0.001) {
        return '< 0.001';
      } else {
        return format('.3f')(pvalue);
      }
    },
    formatPValueText(pvalue) {
      if (pvalue == null) {
        return '';
      }
      if (pvalue < 0.001) {
        return 'p < 0.001';
      } else {
        return 'p = ' + format('.3f')(pvalue);
      }
    },
    formatOddsText(odds, ll, ul) {
      return (
        format('.2f')(odds) +
        ' (' +
        format('.2f')(ll) +
        ', ' +
        format('.2f')(ul) +
        ')'
      );
    },
  },
  props: {
    colorScheme: {
      type: String,
      required: false,
      default: 'val100',
    },
    showReviewHelp: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      highlight_by_pval: false,
      pval_thresholds: [0.001, 0.01, 0.05, 0.1, 1],
      pvt: 'None',
      expanded: true,
      selectedComparisonMeasure: 'First Visit',
      colors: colors,
      showInterpretation: false,
      showHelpChip: false,
      queryToString: queryToString,
      chartData: [],
      chartLayout: {},
      displayedItems: [],
      showTooltips: true,
      showErrorBars: true,
      showOdds: true,
      showPVals: false,
      showLogScale: false,
      hasErrors: false,
      hasCategorical: false,
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('cohortManager', {
      odds_ratio: state.ODDS_RATIO,
      odds_ratio_error: state.ODDS_RATIO_ERROR,
      odds_ratio_request_status: state.ODDS_RATIO_REQUEST_STATUS,
      pval_threshold: state.PVAL_THRESHOLD,
      collection: state.COLLECTION,
      comparisonMeasure: state.COMPARISON_MEASURE,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
      filteredData: state.FILTERED_DATA,
      unfilteredData: state.UNFILTERED_DATA,
      inputVariables: state.INPUT_VARIABLES,
      queries: state.QUERIES,
    }),
    headers() {
      var headers = [
        {
          text: 'Variable',
          align: 'left',
          sortable: true,
          value: 'label',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Error',
          align: 'left',
          sortable: false,
          value: 'err',
          class: 'text-subtitle-1 font-weight-bold',
        },
      ];
      return headers;
    },
    cohortSize() {
      return this.filteredData.length;
    },
    remainderSize() {
      return this.unfilteredData.length - this.filteredData.length;
    },
    variable() {
      return this.inputVariables[0];
    },
  },
  watch: {
    pvt(newPvt) {
      this.setPvalThreshold(newPvt);
    },
    showReviewHelp(show) {
      this.showHelpChip = show;
      this.showInterpretation = show;
    },
    odds_ratio_request_status(newVal) {
      if (newVal == 'loaded') {
        this.renderForestPlot();
      }
    },
    showTooltips() {
      this.renderForestPlot();
    },
    showErrorBars() {
      this.renderForestPlot();
    },
    showOdds() {
      this.renderForestPlot();
    },
    showPVals() {
      this.renderForestPlot();
    },
    showLogScale() {
      this.renderForestPlot();
    },
  },
  created() {
    this.pvt = this.pval_threshold;
    if (this.odds_ratio_request_status == 'loaded') {
      this.renderForestPlot();
    }
  },
  methods: {
    ...mapActions('cohortManager', {
      setPvalThreshold: actions.SET_PVAL_THRESHOLD,
    }),
    renderForestPlot() {
      this.chartData = [];
      this.displayedItems = [];
      this.hasErrors = false;
      this.hasCategorical = false;

      var lineColor = this.pval_table_cell_color(0);

      for (var i = -1; i < this.pval_thresholds.length; ++i) {
        let x = [];
        let x_ll = [];
        let x_ul = [];
        let y = [];
        let text = [];
        let customdata = [];

        var color = null;

        if (i == -1) {
          color = 'black';

          for (const v of this.odds_ratio) {
            if (v.error == null && v.pval == null) {
              x.push(v.odds_ratio);
              x_ll.push(x_ll, v.ll);
              x_ul.push(v.ul);
              y.push(v.abbreviation);
              this.displayedItems.push(v.abbreviation);
              text.push('');
              customdata.push({
                pval: this.$options.filters.formatPValue(v.pval),
                ll: v.ll,
                ul: v.ul,
              });
              this.hasCategorical = true;
            } else if (v.error != null) {
              this.hasErrors = true;
            }
          }
        } else {
          var ll = i == 0 ? -1e-6 : this.pval_thresholds[i - 1];
          var ul = this.pval_thresholds[i];
          color = this.pval_table_cell_color(ll);

          for (const v of this.odds_ratio) {
            if (
              v.error == null &&
              v.pval != null &&
              v.pval > ll &&
              v.pval <= ul
            ) {
              x.push(v.odds_ratio);
              x_ll.push(v.odds_ratio - v.ll);
              x_ul.push(v.ul - v.odds_ratio);
              y.push(v.abbreviation);
              this.displayedItems.push(v.abbreviation);

              if (this.showOdds && !this.showPVals) {
                text.push(
                  this.$options.filters.formatOddsText(v.odds_ratio, v.ll, v.ul)
                );
              } else if (!this.showOdds && this.showPVals) {
                text.push(this.$options.filters.formatPValueText(v.pval));
              } else if (this.showOdds && this.showPVals) {
                text.push(
                  this.$options.filters.formatOddsText(
                    v.odds_ratio,
                    v.ll,
                    v.ul
                  ) +
                    ', ' +
                    this.$options.filters.formatPValueText(v.pval)
                );
              }

              customdata = [
                ...customdata,
                {
                  pval: this.$options.filters.formatPValueText(v.pval),
                  ll: v.ll,
                  ul: v.ul,
                },
              ];
            } else if (v.error != null) {
              this.hasErrors = true;
            }
          }
        }

        let chartData = {
          y: y,
          x: x,
          customdata: customdata,
          mode: 'markers+text',
          type: 'scatter',
        };

        if (i == -1) {
          chartData['marker'] = {
            size: 12,
            symbol: 'square',
            color: color,
          };
        } else {
          chartData['marker'] = {
            size: 12,
            color: color,
            line: {
              color: lineColor,
              width: 2,
            },
          };
        }

        if (this.showTooltips) {
          if (i == -1) {
            chartData['hovertemplate'] = 'Reference Category<extra></extra>';
          } else {
            chartData['hovertemplate'] =
              '%{x:.3f} (%{customdata.ll:.3f}, %{customdata.ul:.3f})<br>%{customdata.pval}<extra></extra>';
          }
        } else {
          chartData['hoverinfo'] = 'skip';
        }

        if (this.showErrorBars) {
          chartData['error_x'] = {
            type: 'data',
            symmetric: false,
            array: x_ul,
            arrayminus: x_ll,
            visible: true,
            color: lineColor,
            thickness: 2,
          };
        }

        if (this.showOdds || this.showPVals) {
          chartData['text'] = text;
          chartData['textposition'] = 'top center';
        }

        this.chartData.push(chartData);
      }

      // get order of variables to display in plot
      const sorter = (a, b) => {
        let [a_var, a_cat] = a.abbreviation.split(' | ');
        let [b_var, b_cat] = b.abbreviation.split(' | ');

        if (a_var > b_var) {
          return -1;
        } else if (a_var < b_var) {
          return 1;
        } else {
          let a_ref = a_cat.endsWith('*');
          a_cat = a_cat.replace(' *', '');

          let b_ref = b_cat.endsWith('*');
          b_cat = b_cat.replace(' *', '');

          if (a_ref && !b_ref) {
            return 1;
          } else if (!a_ref && b_ref) {
            return -1;
          } else {
            if (a_cat > b_cat) {
              return -1;
            } else {
              return 1;
            }
          }
        }
      };
      this.odds_ratio.sort(sorter);

      var order = this.odds_ratio
        .map(a => a.abbreviation)
        .filter(a => this.displayedItems.includes(a));

      var numDisplayed = this.displayedItems.length;
      var rowHeight = numDisplayed > 1 ? 50 : 80;
      this.chartLayout = {
        showlegend: false,
        responsive: true,
        autosize: true,
        height: rowHeight * numDisplayed + 90,
        shapes: [
          {
            type: 'line',
            x0: 1,
            y0: -0.5,
            x1: 1,
            y1: numDisplayed,
            line: {
              color: 'rgba(0,0,0,0.4)',
              width: 2,
              dash: 'dash',
            },
            layer: 'between',
          },
        ],
        margin: {
          t: 0,
          r: 40,
          b: 100,
        },
        yaxis: {
          zeroline: false,
          automargin: true,
          range: [-0.5, numDisplayed],
          fixedrange: true,
          categoryorder: 'array',
          categoryarray: order,
        },
        xaxis: {
          zeroline: false,
          automargin: true,
          title: 'Odds Ratio',
          fixedrange: false,
        },
        hoverlabel: {
          bgcolor: '#ffffff',
          margin: {
            l: 10,
          },
          font: {
            family: 'Roboto, sans-serif',
            size: 16,
          },
        },
        font: {
          family: 'Roboto, sans-serif',
          size: 16,
        },
        modebar: {
          remove: ['lasso', 'select'],
        },
        annotations: [
          {
            xref: 'paper',
            yref: 'paper',
            x: 1,
            xanchor: 'right',
            y: 0,
            yanchor: 'center',
            yshift: -55,
            xshift: 0,
            ay: 0,
            ax: -20,
            arrowhead: 2,
            text:
              'Increasing likelihood<br>of being in selected<br> study group',
            showarrow: true,
          },
          {
            xref: 'paper',
            yref: 'paper',
            x: 0,
            xanchor: 'left',
            y: 0,
            yanchor: 'center',
            yshift: -55,
            xshift: 0,
            ay: 0,
            ax: 20,
            arrowhead: 2,
            text:
              'Decreasing likelihood<br>of being in selected<br> study group',
            showarrow: true,
          },
        ],
      };

      if (this.showLogScale) {
        this.chartLayout['xaxis']['type'] = 'log';
      }
    },
    updatePval(newPval) {
      this.setPvalThreshold(newPval);
    },
    getVariableClass(v) {
      if (v.pval != null && v.pval < this.pval_threshold && !v.error) {
        return 'highlight-var-row';
      }
      return '';
    },
    getPvalColor(pv) {
      var c = colors['pvals'][pv.toString() + '-' + this.colorScheme];
      if (c) {
        return c['color'];
      } else {
        return 'white';
      }
    },
    expandClicked() {
      this.expanded = !this.expanded;
    },
    pval_table_cell_aux(pval, which) {
      let ccl = this.colors['pvals']['1' + '-' + this.colorScheme][which];
      if (pval == null) {
        return ccl;
      } else {
        if (pval < 0.001) {
          ccl = this.colors['pvals']['0.001' + '-' + this.colorScheme][which];
        } else if (pval < 0.01) {
          ccl = this.colors['pvals']['0.01' + '-' + this.colorScheme][which];
        } else if (pval < 0.05) {
          ccl = this.colors['pvals']['0.05' + '-' + this.colorScheme][which];
        } else if (pval < 0.1) {
          ccl = this.colors['pvals']['0.1' + '-' + this.colorScheme][which];
        }
        return ccl;
      }
    },
    pval_table_cell_class(pval) {
      return this.pval_table_cell_aux(pval, 'class');
    },
    pval_table_cell_color(pval) {
      return this.pval_table_cell_aux(pval, 'color');
    },
    pval_table_text_color(pval) {
      if (this.colorScheme == 'brewer5') {
        if (pval != null && pval < 0.001) {
          return 'white';
        }
      }
      return 'black';
    },
  },
};
</script>

<style>
table.v-data-table thead tr th {
  font-size: 24px;
}

table.v-data-table tbody tr td {
  font-size: 24px;
}

/* Transition effect for changing routes */
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
